import { getSplitSmoothingStrokes, SPLIT_SMOOTHING_OPTIONS } from '@/lib/strokeSplit';
import { getForceUnit, setForceUnit, FORCE_UNITS } from '@/lib/forceUnit';
import { StrokeSensorLogger } from '@/components/StrokeSensorLogger';
import { Pm5GattCard } from '@/components/Pm5GattCard';
import { Pm5InfoCard } from '@/components/Pm5InfoCard';
import { RawMuxLogger } from '@/components/RawMuxLogger';
import { listPm5Gatt } from '@/lib/pm5ForceCurve';
import { readPm5DeviceInfo, writePm5SampleRate } from '@/lib/pm5DeviceInfo';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bluetooth, Satellite, CheckCircle, Activity, Play, AlertCircle, Moon, Sun, Ruler, Clock, Smartphone, MapPin, ExternalLink, Unlink, Link2, Volume2, VolumeX, Nfc, Mic, MicOff, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { PM5Connection } from '@/lib/pm5Connection';
import TopNav from '@/components/TopNav';
import OarlockDeviceCard from '@/components/OarlockDeviceCard';
import CompanyFooter from '@/components/CompanyFooter';
import WBTRecordCard from '@/components/WBTRecordCard';
import { useTheme } from '@/contexts/ThemeContext';
import { useGPS, GPS_SOURCE } from '@/hooks/useGPS';
import { useAudioNotifications } from '@/hooks/useAudioNotifications';
import { isCoxVoiceEnabled, setCoxVoiceEnabled, COX_VOICE_PROFILES, getCoxVoiceProfile, setCoxVoiceProfile, previewCoxVoiceProfile } from '@/hooks/useRaceMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Connected fitness apps configuration
const FITNESS_APPS = [
  { 
    id: 'strava', 
    name: 'Strava', 
    description: 'Upload workouts directly',
    color: '#FC4C02',
    bgClass: 'bg-orange-100 dark:bg-orange-900',
    textClass: 'text-orange-600 dark:text-orange-400',
    hasRealIntegration: true
  },
  { 
    id: 'intervals', 
    name: 'Intervals.icu', 
    description: 'Best rowing analytics',
    color: '#6366F1',
    bgClass: 'bg-indigo-100 dark:bg-indigo-900',
    textClass: 'text-indigo-600 dark:text-indigo-400',
    hasRealIntegration: true
  },
];

// Device card component - moved outside to prevent re-renders
function DeviceCard({ icon: Icon, title, description, connected, connectingState, onConnect, onDisconnect, testId, isDemo = false, extraBadge = null }) {
  return (
    <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${connected ? 'bg-green-100 dark:bg-green-900' : 'bg-zinc-100 dark:bg-zinc-700'}`}>
            <Icon className={`w-4 h-4 ${connected ? 'text-green-600 dark:text-green-400' : 'text-zinc-500 dark:text-zinc-400'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                {title}
              </h2>
              {extraBadge}
            </div>
            {connected ? (
              <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium">
                <CheckCircle className="w-3 h-3" />
                <span>{isDemo ? 'Active' : (typeof connected === 'string' ? connected : 'Connected')}</span>
              </div>
            ) : (
              <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate max-w-[180px]">{description}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {/* Disconnect button - only show when connected and has onDisconnect */}
          {connected && onDisconnect && !isDemo && (
            <button
              onClick={onDisconnect}
              className="px-3 py-2 rounded-lg font-bold text-xs transition-colors bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800"
              data-testid={`${testId}-disconnect`}
            >
              Disconnect
            </button>
          )}
          <button
            onClick={onConnect}
            disabled={connectingState || (connected && !isDemo && !onDisconnect)}
            className={`px-4 py-2 rounded-lg font-bold text-xs transition-colors ${
              connected && !isDemo
                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400'
                : isDemo && connected
                ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-800'
                : 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200'
            } disabled:opacity-50`}
          >
            {connectingState ? '...' : connected && !isDemo ? 'Connected' : isDemo ? (connected ? 'Off' : 'On') : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DeviceConnection() {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const [hrDevice, setHrDevice] = useState(null);
  const [pm5Device, setPm5Device] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [demoBoost, setDemoBoost] = useState(false);
  // Current boat + mode read from localStorage so the Settings-page WBT
  // Record card knows which category to look up. Kept in state so
  // storage-events keep it fresh when the rower changes boat on the
  // splash screen and returns to Settings.
  const [currentBoat, setCurrentBoat] = useState(() =>
    localStorage.getItem('default_boat_type') || null);
  const isErgo = currentBoat === 'c2_erg';
  const [connecting, setConnecting] = useState({ hr: false, gps: false, pm5: false });
  
  // GPS with fallback support
  const gps = useGPS();
  const [gpsPreference, setGpsPreference] = useState(() => localStorage.getItem('gps_preference') || 'auto'); // 'auto', 'external', 'phone'
  
  // Audio notifications
  const audio = useAudioNotifications();

  // Cox voice (Race mode encouragement cues) — persisted via useRaceMode helpers
  const [coxVoiceEnabled, setCoxVoiceEnabledState] = useState(() => isCoxVoiceEnabled());
  // Sync across tabs when the flag changes elsewhere
  useEffect(() => {
    const onChange = (e) => setCoxVoiceEnabledState(!!e?.detail?.enabled);
    window.addEventListener('crewz-cox-voice-changed', onChange);
    return () => window.removeEventListener('crewz-cox-voice-changed', onChange);
  }, []);
  const handleToggleCoxVoice = () => {
    const next = !coxVoiceEnabled;
    setCoxVoiceEnabledState(next);
    setCoxVoiceEnabled(next);
  };

  // Cox voice profile (2 male / 2 female)
  const [coxVoiceProfile, setCoxVoiceProfileState] = useState(() => getCoxVoiceProfile());
  useEffect(() => {
    const onChange = (e) => setCoxVoiceProfileState(e?.detail?.profileId || getCoxVoiceProfile());
    window.addEventListener('crewz-cox-voice-profile-changed', onChange);
    return () => window.removeEventListener('crewz-cox-voice-profile-changed', onChange);
  }, []);
  const handlePickVoiceProfile = (profileId) => {
    setCoxVoiceProfileState(profileId);
    setCoxVoiceProfile(profileId);
    // Audition the voice immediately — explicit user action bypasses the
    // global cox-voice toggle so coaches can preview while cox is off.
    previewCoxVoiceProfile(profileId);
  };
  const handleTestCoxVoice = () => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      toast.error('Speech synthesis not supported on this device');
      return;
    }
    const u = new window.SpeechSynthesisUtterance('This is your race — take it!');
    u.rate = 1.0;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };
  
  // User Preferences
  const [distanceUnit, setDistanceUnit] = useState(() => localStorage.getItem('distance_unit') || 'km');
  const [clockFormat, setClockFormat] = useState(() => localStorage.getItem('clock_format') || '12');
  // Show/hide the Check Factor (CF%) bar on the Monitor. Kept for
  // backwards compat — new UI uses the 3-option `leftTileDisplay` below.
  const [showCheckFactor, setShowCheckFactor] = useState(() => {
    try {
      const raw = localStorage.getItem('show_check_factor');
      return raw === 'true';
    } catch { return false; }
  });
  // Left flanking tile — "off" / "check_factor" / "session_goal".
  // Persisted to localStorage AND the backend preferences record.
  const [leftTileDisplay, setLeftTileDisplay] = useState(() => {
    try {
      const raw = localStorage.getItem('left_tile_display');
      if (raw === 'off' || raw === 'check_factor' || raw === 'session_goal') return raw;
      const legacy = localStorage.getItem('show_check_factor');
      return legacy === 'true' ? 'check_factor' : 'off';
    } catch { return 'off'; }
  });
  
  // Split smoothing — strokes averaged for the live split (non-Erg modes)
  const [splitSmoothing, setSplitSmoothing] = useState(() => getSplitSmoothingStrokes());
  const handleSplitSmoothingChange = (n) => {
    setSplitSmoothing(n);
    localStorage.setItem('split_smoothing_strokes', String(n));
    savePreferences({ split_smoothing_strokes: n });
  };

  const [forceUnit, setForceUnitState] = useState(() => getForceUnit());
  const handleForceUnitChange = (u) => {
    setForceUnitState(u);
    setForceUnit(u);
  };

  // Connected Apps state
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaAthlete, setStravaAthlete] = useState(null);
  const [stravaLoading, setStravaLoading] = useState(false);
  
  // Intervals.icu state
  const [intervalsConnected, setIntervalsConnected] = useState(false);
  const [intervalsAthlete, setIntervalsAthlete] = useState(null);
  const [intervalsLoading, setIntervalsLoading] = useState(false);
  const [showIntervalsApiKeyInput, setShowIntervalsApiKeyInput] = useState(false);
  const [intervalsApiKey, setIntervalsApiKey] = useState('');

  // Check for Strava callback params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stravaStatus = params.get('strava');
    
    if (stravaStatus === 'connected') {
      toast.success('Successfully connected to Strava!');
      window.history.replaceState({}, '', '/settings');
      // Reload Strava status
      loadStravaStatus();
    } else if (stravaStatus === 'error') {
      const message = params.get('message') || 'Failed to connect to Strava';
      toast.error(message);
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  // Load Strava connection status
  const loadStravaStatus = async () => {
    try {
      const response = await fetch(`${API}/strava/status`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStravaConnected(data.connected);
        if (data.connected) {
          setStravaAthlete(data.athlete_name);
        }
      }
    } catch (error) {
      console.error('Failed to load Strava status:', error);
    }
  };
  
  // Load Intervals.icu connection status
  const loadIntervalsStatus = async () => {
    try {
      const response = await fetch(`${API}/intervals/status`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setIntervalsConnected(data.connected);
        if (data.connected) {
          setIntervalsAthlete(data.athlete_name);
        }
      }
    } catch (error) {
      console.error('Failed to load Intervals.icu status:', error);
    }
  };
  
  // Connect to Intervals.icu
  const connectIntervals = async () => {
    if (!intervalsApiKey.trim()) {
      toast.error('Please enter your Intervals.icu API key');
      return;
    }
    
    setIntervalsLoading(true);
    try {
      const response = await fetch(`${API}/intervals/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ api_key: intervalsApiKey.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIntervalsConnected(true);
        setIntervalsAthlete(data.athlete_name);
        setShowIntervalsApiKeyInput(false);
        setIntervalsApiKey('');
        toast.success(data.message || 'Connected to Intervals.icu!');
      } else {
        toast.error(data.detail || 'Failed to connect to Intervals.icu');
      }
    } catch (error) {
      console.error('Intervals.icu connect error:', error);
      toast.error('Failed to connect to Intervals.icu');
    } finally {
      setIntervalsLoading(false);
    }
  };
  
  // Disconnect from Intervals.icu
  const disconnectIntervals = async () => {
    try {
      const response = await fetch(`${API}/intervals/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setIntervalsConnected(false);
        setIntervalsAthlete(null);
        toast.success('Disconnected from Intervals.icu');
      }
    } catch (error) {
      console.error('Intervals.icu disconnect error:', error);
      toast.error('Failed to disconnect');
    }
  };

  // Load preferences from backend on mount
  // NOTE: PM5 and HR devices default to disconnected on startup (user requested).
  // We only load user preferences (distance_unit, clock_format, etc.), not device states.
  // However, we DO restore connection state if the device is STILL connected (e.g., after navigating away and back)
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        // Try to load from backend first
        const response = await fetch(`${API}/users/preferences`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const prefs = await response.json();
          
          // NOTE: We intentionally do NOT restore PM5/HR device states on startup.
          // The user wants devices to default to disconnected and require manual connection.
          // Only demo_mode and GPS preference are restored as they don't require hardware.
          
          if (prefs.demo_mode) {
            setDemoMode(prefs.demo_mode);
            localStorage.setItem('demo_mode', 'true');
          }
          if (prefs.gps_preference) {
            setGpsPreference(prefs.gps_preference);
            localStorage.setItem('gps_preference', prefs.gps_preference);
          }
          
          // Load user preferences
          if (prefs.distance_unit) {
            setDistanceUnit(prefs.distance_unit);
            localStorage.setItem('distance_unit', prefs.distance_unit);
          }
          if (prefs.clock_format) {
            setClockFormat(prefs.clock_format);
            localStorage.setItem('clock_format', prefs.clock_format);
          }
          // Show check factor preference — persisted on backend as boolean.
          if (typeof prefs.show_check_factor === 'boolean') {
            setShowCheckFactor(prefs.show_check_factor);
            localStorage.setItem('show_check_factor', String(prefs.show_check_factor));
          }
          if (typeof prefs.left_tile_display === 'string') {
            setLeftTileDisplay(prefs.left_tile_display);
            localStorage.setItem('left_tile_display', prefs.left_tile_display);
          }
          if (SPLIT_SMOOTHING_OPTIONS.includes(prefs.split_smoothing_strokes)) {
            setSplitSmoothing(prefs.split_smoothing_strokes);
            localStorage.setItem('split_smoothing_strokes', String(prefs.split_smoothing_strokes));
          }
          return;
        }
      } catch (error) {
        console.log('Backend preferences not available, using localStorage');
      }
      
      // Fallback to localStorage for non-device preferences only
      // NOTE: We do NOT restore PM5/HR states from localStorage either - devices default to disconnected.
      
      const savedDemoMode = localStorage.getItem('demo_mode');
      if (savedDemoMode === 'true') {
        setDemoMode(true);
      }
      // Demo Boost hydration — mirrors demo_mode pattern.
      if (localStorage.getItem('demo_boost') === 'true') {
        setDemoBoost(true);
      }
      
      // Load GPS preference from localStorage
      const savedGpsPreference = localStorage.getItem('gps_preference');
      if (savedGpsPreference) {
        setGpsPreference(savedGpsPreference);
      }
      
      // Load user preferences from localStorage
      const savedDistanceUnit = localStorage.getItem('distance_unit');
      if (savedDistanceUnit) {
        setDistanceUnit(savedDistanceUnit);
      }
      const savedClockFormat = localStorage.getItem('clock_format');
      if (savedClockFormat) {
        setClockFormat(savedClockFormat);
      }
      const savedShowCheckFactor = localStorage.getItem('show_check_factor');
      if (savedShowCheckFactor !== null) {
        setShowCheckFactor(savedShowCheckFactor === 'true');
      }
    };
    
    // Check for ACTIVE device connections (when navigating back to Settings page)
    // This restores the UI state if devices are still connected from earlier in the session
    const checkActiveConnections = () => {
      // Check if HR is still connected
      if (window.hrConnection && window.hrConnection.server && window.hrConnection.server.connected) {
        const hrName = window.hrConnection.device?.name || 'Heart Rate Monitor';
        setHrDevice(hrName);
        console.log('📶 HR monitor still connected:', hrName);
      } else {
        // Clear stale HR state
        setHrDevice(null);
        localStorage.removeItem('hr_device_connected');
        localStorage.removeItem('hr_device_name');
      }
      
      // Check if PM5 is still connected  
      if (window.pm5Connection && window.pm5Connection.isConnected) {
        const pm5Name = window.pm5Connection.device?.name || 'Concept2 PM5';
        setPm5Device(pm5Name);
        console.log('📶 PM5 still connected:', pm5Name);
      } else {
        // Clear stale PM5 state
        setPm5Device(null);
        localStorage.removeItem('pm5_device_connected');
        localStorage.removeItem('pm5_device_name');
      }
    };
    
    checkActiveConnections();
    loadPreferences();
    loadStravaStatus();
    loadIntervalsStatus();
  }, []);

  // Save preferences to backend
  const savePreferences = async (updates) => {
    try {
      await fetch(`${API}/users/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
    } catch (error) {
      console.log('Failed to save preferences to backend');
    }
  };

  // Connect to Strava
  const connectStrava = async () => {
    try {
      setStravaLoading(true);
      const response = await fetch(`${API}/strava/connect`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Redirect to Strava OAuth
        window.location.href = data.auth_url;
      } else {
        toast.error('Failed to initiate Strava connection');
      }
    } catch (error) {
      console.error('Strava connect error:', error);
      toast.error('Failed to connect to Strava');
    } finally {
      setStravaLoading(false);
    }
  };

  // Disconnect from Strava
  const disconnectStrava = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Strava?')) return;
    
    try {
      const response = await fetch(`${API}/strava/disconnect`, { 
        method: 'POST',
        credentials: 'include' 
      });
      if (response.ok) {
        setStravaConnected(false);
        setStravaAthlete(null);
        toast.success('Disconnected from Strava');
      }
    } catch (error) {
      toast.error('Failed to disconnect from Strava');
    }
  };

  const connectHeartRate = async () => {
    setConnecting({ ...connecting, hr: true });
    try {
      if (!navigator.bluetooth) {
        throw new Error('Web Bluetooth API not supported in this browser');
      }

      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: ['heart_rate'] }]
      });

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('heart_rate');
      const characteristic = await service.getCharacteristic('heart_rate_measurement');

      const deviceName = device.name || 'Heart Rate Monitor';
      setHrDevice(deviceName);
      localStorage.setItem('hr_device_connected', 'true');
      localStorage.setItem('hr_device_name', deviceName);
      savePreferences({ last_hr_device: deviceName });
      
      // Store the device reference globally for disconnect functionality
      window.hrConnection = {
        device: device,
        server: server,
        characteristic: characteristic,
        disconnect: async () => {
          try {
            if (server && server.connected) {
              await server.disconnect();
            }
          } catch (e) {
            console.error('Error during HR disconnect:', e);
          }
        }
      };
      
      toast.success('Heart rate monitor connected');

      characteristic.addEventListener('characteristicvaluechanged', (event) => {
        const value = event.target.value;
        const heartRate = value.getUint8(1);
        window.dispatchEvent(new CustomEvent('heart-rate-update', { detail: heartRate }));
      });

      await characteristic.startNotifications();

      device.addEventListener('gattserverdisconnected', () => {
        setHrDevice(null);
        localStorage.removeItem('hr_device_connected');
        localStorage.removeItem('hr_device_name');
        window.hrConnection = null;
        toast.error('Heart rate monitor disconnected');
      });
    } catch (error) {
      console.error('Failed to connect heart rate monitor:', error);
      toast.error(error.message || 'Failed to connect heart rate monitor');
    } finally {
      setConnecting({ ...connecting, hr: false });
    }
  };

  // Handle GPS preference change
  const handleGpsPreferenceChange = async (preference) => {
    setGpsPreference(preference);
    localStorage.setItem('gps_preference', preference);
    savePreferences({ gps_preference: preference });
    
    // If currently tracking, restart with new preference
    if (gps.isTracking) {
      await gps.stopTracking();
      
      if (preference === 'phone') {
        gps.usePhoneGPSOnly();
        toast.success('Switched to phone GPS');
      } else if (preference === 'external') {
        const success = await gps.connectExternalGPS();
        if (success) {
          toast.success('Connected to external GPS');
        } else {
          toast.error('Failed to connect external GPS');
        }
      } else {
        // Auto mode
        await gps.startTracking(true);
        toast.success('GPS set to auto mode (external with phone fallback)');
      }
    }
  };

  // Test GPS connection
  const testGPS = async () => {
    setConnecting({ ...connecting, gps: true });
    try {
      if (gpsPreference === 'phone') {
        gps.usePhoneGPSOnly();
        toast.success('Phone GPS activated');
      } else if (gpsPreference === 'external') {
        const success = await gps.connectExternalGPS();
        if (success) {
          toast.success('External GPS connected');
        } else {
          toast.error('Failed to connect external GPS. Try phone GPS instead.');
        }
      } else {
        // Auto mode - try external first, fallback to phone
        await gps.startTracking(true);
        toast.success('GPS tracking started (auto mode)');
      }
    } catch (error) {
      console.error('GPS test error:', error);
      toast.error(error.message || 'GPS connection failed');
    } finally {
      setConnecting({ ...connecting, gps: false });
    }
  };

  const connectPM5 = async () => {
    setConnecting({ ...connecting, pm5: true });
    try {
      const pm5 = new PM5Connection();
      await pm5.connect();
      await pm5.startNotifications();

      const deviceName = pm5.device.name || 'Concept2 PM5';
      setPm5Device(deviceName);
      localStorage.setItem('pm5_device_connected', 'true');
      localStorage.setItem('pm5_device_name', deviceName);
      savePreferences({ last_pm5_device: deviceName });
      window.pm5Connection = pm5;
      await listPm5Gatt(pm5);
      // Read firmware + serial, then set the fastest status sample rate.
      // Sequential awaits avoid racing Chrome's single GATT operation queue.
      await readPm5DeviceInfo(pm5);
      await writePm5SampleRate(pm5);
      toast.success('PM5 connected successfully');

      pm5.on('rowing-status', (data) => {
        window.dispatchEvent(new CustomEvent('pm5-rowing-status', { detail: data }));
      });

      pm5.on('extra-rowing-data', (data) => {
        window.dispatchEvent(new CustomEvent('pm5-extra-data', { detail: data }));
      });

      pm5.on('general-status', (data) => {
        window.dispatchEvent(new CustomEvent('pm5-general-status', { detail: data }));
      });

      pm5.on('disconnected', () => {
        setPm5Device(null);
        localStorage.removeItem('pm5_device_connected');
        localStorage.removeItem('pm5_device_name');
        window.pm5Connection = null;
        toast.error('PM5 disconnected');
      });
    } catch (error) {
      console.error('Failed to connect PM5:', error);
      
      // Show more helpful error message with troubleshooting tips
      const errorMsg = error.message || 'Failed to connect to PM5';
      
      if (errorMsg.includes('not supported') || errorMsg.includes('GATT')) {
        toast(
          <div className="flex flex-col gap-2">
            <div className="font-bold text-red-600">PM5 Connection Error</div>
            <div className="text-sm">GATT operation not supported</div>
            <div className="text-xs text-zinc-500 mt-1">
              <strong>Try these steps:</strong>
              <ol className="list-decimal list-inside mt-1">
                <li>Update PM5 firmware via Concept2 Utility app</li>
                <li>Put PM5 in Bluetooth pairing mode</li>
                <li>Use Chrome or Edge browser</li>
                <li>Forget device in phone Bluetooth settings and retry</li>
              </ol>
            </div>
          </div>,
          { duration: 10000 }
        );
      } else if (errorMsg.includes('cancelled')) {
        toast.info('Connection cancelled');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setConnecting({ ...connecting, pm5: false });
    }
  };

  const toggleDemoMode = () => {
    const newMode = !demoMode;
    setDemoMode(newMode);
    localStorage.setItem('demo_mode', newMode.toString());
    savePreferences({ demo_mode: newMode });
    // Broadcast so any already-mounted page (e.g. WorkoutMonitor in another
    // tab or a back-stack route) picks up the change immediately instead of
    // waiting for a fresh mount.
    try {
      window.dispatchEvent(new CustomEvent('crewz-demo-mode-changed', { detail: newMode }));
    } catch (_e) { /* noop */ }
    toast.success(newMode ? 'Demo mode enabled' : 'Demo mode disabled');
  };

  // Demo Boost — a per-session speed multiplier on the demo simulator so
  // rowers can preview the WBT Crown without needing to actually be at
  // world-record pace. Only affects the demo simulator; ignored when a
  // real PM5 is connected.
  const toggleDemoBoost = () => {
    if (!demoMode) {
      toast.info('Enable Demo Mode first');
      return;
    }
    const newBoost = !demoBoost;
    setDemoBoost(newBoost);
    localStorage.setItem('demo_boost', newBoost.toString());
    try {
      window.dispatchEvent(new CustomEvent('crewz-demo-boost-changed', { detail: newBoost }));
    } catch (_e) { /* noop */ }
    toast.success(newBoost ? 'Demo Boost enabled — WBT Crown should now trigger' : 'Demo Boost disabled');
  };

  // ── Demo Boost cross-page sync ────────────────────────────────────────
  // The mount-only hydration effect below only runs the FIRST time the
  // DeviceConnection page mounts. If the rower turns Boost on, navigates
  // to /monitor, then comes back to /devices, React can keep the page
  // mounted (route cache) but the previous `demoBoost` state is stale
  // relative to localStorage. This listener re-reads on every focus and
  // on any localStorage change so the toggle button always reflects
  // truth. Fixes iter-66 "Demo Boost keeps resetting" report.
  useEffect(() => {
    const readBoost = () => setDemoBoost(localStorage.getItem('demo_boost') === 'true');
    const onStorage = (e) => { if (e.key === 'demo_boost') readBoost(); };
    const onCustom = (e) => setDemoBoost(!!e.detail);
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', readBoost);
    window.addEventListener('crewz-demo-boost-changed', onCustom);
    // Also read on mount in case the mount-time hydration below hasn't
    // executed yet (async user-prefs load can race with first paint).
    readBoost();
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', readBoost);
      window.removeEventListener('crewz-demo-boost-changed', onCustom);
    };
  }, []);

  const disconnectPM5 = async () => {
    try {
      if (window.pm5Connection) {
        await window.pm5Connection.disconnect();
      }
      setPm5Device(null);
      localStorage.removeItem('pm5_device_connected');
      localStorage.removeItem('pm5_device_name');
      window.pm5Connection = null;
      toast.success('PM5 disconnected');
    } catch (error) {
      console.error('Error disconnecting PM5:', error);
      // Force clear even if disconnect fails
      setPm5Device(null);
      localStorage.removeItem('pm5_device_connected');
      localStorage.removeItem('pm5_device_name');
      window.pm5Connection = null;
      toast.success('PM5 disconnected');
    }
  };

  const disconnectHR = async () => {
    try {
      if (window.hrConnection) {
        await window.hrConnection.disconnect();
      }
      setHrDevice(null);
      localStorage.removeItem('hr_device_connected');
      localStorage.removeItem('hr_device_name');
      window.hrConnection = null;
      toast.success('Heart Rate Monitor disconnected');
    } catch (error) {
      console.error('Error disconnecting HR:', error);
      setHrDevice(null);
      localStorage.removeItem('hr_device_connected');
      localStorage.removeItem('hr_device_name');
      window.hrConnection = null;
      toast.success('Heart Rate Monitor disconnected');
    }
  };

  // Handle preference changes
  const handleDistanceUnitChange = (unit) => {
    setDistanceUnit(unit);
    localStorage.setItem('distance_unit', unit);
    savePreferences({ distance_unit: unit });
  };

  const handleClockFormatChange = (format) => {
    setClockFormat(format);
    localStorage.setItem('clock_format', format);
    savePreferences({ clock_format: format });
  };

  const handleShowCheckFactorChange = (next) => {
    setShowCheckFactor(next);
    localStorage.setItem('show_check_factor', String(next));
    try {
      window.dispatchEvent(new CustomEvent('crewz:show_check_factor_changed', { detail: next }));
    } catch (_e) { /* noop */ }
    savePreferences({ show_check_factor: next });
  };

  // New: single tri-state control for the Monitor's left flanking tile.
  // Replaces the CF-only toggle above (kept for legacy callers) with a
  // proper mutually-exclusive selector: Off / Check Factor / Session Goal.
  const handleLeftTileChange = (next) => {
    setLeftTileDisplay(next);
    localStorage.setItem('left_tile_display', next);
    // Keep the legacy `show_check_factor` flag in sync so any code that
    // still reads it doesn't drift out of alignment.
    const cf = next === 'check_factor';
    setShowCheckFactor(cf);
    localStorage.setItem('show_check_factor', String(cf));
    try {
      window.dispatchEvent(new CustomEvent('crewz:left_tile_display_changed', { detail: next }));
      window.dispatchEvent(new CustomEvent('crewz:show_check_factor_changed', { detail: cf }));
    } catch (_e) { /* noop */ }
    savePreferences({ left_tile_display: next, show_check_factor: cf });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-900 flex flex-col transition-colors">
      <TopNav />
      <div className="flex-1 py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-extrabold tracking-tighter text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Settings
            </h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">Customize your preferences and connect devices</p>
          </div>

          {/* Devices Section - At the top */}
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-3 text-zinc-700 dark:text-zinc-300" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Devices
            </h2>
            {/* 2x2 Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DeviceCard
              icon={Activity}
              title="Concept2 PM5"
              description="Bluetooth rowing data"
              connected={pm5Device}
              connectingState={connecting.pm5}
              onConnect={connectPM5}
              onDisconnect={disconnectPM5}
              testId="pm5-device-section"
            />

            <DeviceCard
              icon={Bluetooth}
              title="Heart Rate Monitor"
              description="BLE heart rate service"
              connected={hrDevice}
              connectingState={connecting.hr}
              onConnect={connectHeartRate}
              onDisconnect={disconnectHR}
              testId="hr-device-section"
              extraBadge={
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-700 text-[9px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400"
                  data-testid="hr-nfc-tap-to-pair"
                  title="Tap-to-pair via the NFC tag (NTAG215) on the right edge of the CREWZ case. Available once the case is paired."
                >
                  <Nfc className="w-3 h-3" />
                  Tap to Pair
                </span>
              }
            />

            {/* GPS with Fallback Support */}
            <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm p-4" data-testid="gps-device-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    gps.hasGPS ? 'bg-green-100 dark:bg-green-900' : 'bg-zinc-100 dark:bg-zinc-700'
                  }`}>
                    {gps.isPhoneGPS ? (
                      <Smartphone className={`w-4 h-4 ${gps.hasGPS ? 'text-green-600 dark:text-green-400' : 'text-zinc-500'}`} />
                    ) : (
                      <Satellite className={`w-4 h-4 ${gps.hasGPS ? 'text-green-600 dark:text-green-400' : 'text-zinc-500'}`} />
                    )}
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                      GPS Tracking
                    </h2>
                    {gps.hasGPS ? (
                      <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        <span>
                          {gps.isExternalGPS ? 'CREWZ Case GPS (25Hz)' : 'Phone GPS'}
                          {gps.accuracy && ` · ±${Math.round(gps.accuracy)}m`}
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-400 dark:text-zinc-500">Auto-fallback to phone GPS</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={testGPS}
                  disabled={connecting.gps}
                  className={`px-4 py-2 rounded-lg font-bold text-xs transition-colors ${
                    gps.hasGPS
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400'
                      : 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200'
                  } disabled:opacity-50`}
                  data-testid="gps-test-btn"
                >
                  {connecting.gps ? '...' : gps.hasGPS ? 'Active' : 'Test'}
                </button>
              </div>
              
              {/* GPS Source Selection */}
              <div className="flex bg-zinc-100 dark:bg-zinc-700 rounded-lg p-0.5">
                <button
                  onClick={() => handleGpsPreferenceChange('auto')}
                  data-testid="gps-auto"
                  className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-colors flex items-center justify-center gap-1 ${
                    gpsPreference === 'auto' 
                      ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm' 
                      : 'text-zinc-500 dark:text-zinc-400'
                  }`}
                >
                  <MapPin className="w-3 h-3" />
                  Auto
                </button>
                <button
                  onClick={() => handleGpsPreferenceChange('external')}
                  data-testid="gps-external"
                  disabled={!gps.supportsExternalGPS}
                  className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-colors flex items-center justify-center gap-1 ${
                    gpsPreference === 'external' 
                      ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm' 
                      : 'text-zinc-500 dark:text-zinc-400'
                  } disabled:opacity-40`}
                >
                  <Satellite className="w-3 h-3" />
                  CREWZ Case
                </button>
                <button
                  onClick={() => handleGpsPreferenceChange('phone')}
                  data-testid="gps-phone"
                  disabled={!gps.supportsPhoneGPS}
                  className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-colors flex items-center justify-center gap-1 ${
                    gpsPreference === 'phone' 
                      ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm' 
                      : 'text-zinc-500 dark:text-zinc-400'
                  } disabled:opacity-40`}
                >
                  <Smartphone className="w-3 h-3" />
                  Phone
                </button>
              </div>
              
              {/* GPS Status Info */}
              {gps.hasGPS && gps.position && (
                <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 font-mono">
                  {gps.position.lat?.toFixed(6)}, {gps.position.lng?.toFixed(6)}
                  {gps.speed !== null && ` · ${gps.speed.toFixed(2)} m/s`}
                </div>
              )}
            </div>

            <DeviceCard
              icon={Play}
              title="Demo Mode"
              description="Simulate rowing data"
              connected={demoMode ? 'Demo Mode' : null}
              connectingState={false}
              onConnect={toggleDemoMode}
              testId="demo-mode-section"
              isDemo={true}
            />

            {/* WBT Record card — CrewZ v4 spec. Only ever renders ONE card
                per profile × boat combination. Empty state (no matching
                record) shows a friendly placeholder rather than the
                silent state used by the crown. */}
            <WBTRecordCard boatClass={currentBoat} isErgo={isErgo} />

            {/* Demo Boost — scales the demo simulator's baseline speed up
                so the WBT Crown will trigger at any category. Only
                visible when Demo Mode is on. */}
            {demoMode && (
              <DeviceCard
                icon={Play}
                title="Demo Boost"
                description="Scale demo speed up to trigger the WBT Crown"
                connected={demoBoost ? 'Boost On' : null}
                connectingState={false}
                onConnect={toggleDemoBoost}
                testId="demo-boost-section"
                isDemo={true}
              />
            )}

            {/* Smart-gate oarlocks (NK EmPower & XBoat) - full-width expandable cards */}
            <OarlockDeviceCard vendor="xboat" />
            <OarlockDeviceCard vendor="nk_empower" comingSoon />
            </div>
          </div>

          {/* User Preferences Section */}
          <div className="mb-6">
            <h2 className="text-lg font-bold mb-3 text-zinc-700 dark:text-zinc-300" style={{ fontFamily: 'Archivo, sans-serif' }}>
              User Preferences
            </h2>
            <div className="space-y-2">
              {/* Distance Unit */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Ruler className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Distance</span>
                </div>
                <div className="flex bg-zinc-100 dark:bg-zinc-700 rounded-lg p-0.5">
                  <button
                    onClick={() => handleDistanceUnitChange('km')}
                    data-testid="distance-km"
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${
                      distanceUnit === 'km' 
                        ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm' 
                        : 'text-zinc-500 dark:text-zinc-400'
                    }`}
                  >
                    KM
                  </button>
                  <button
                    onClick={() => handleDistanceUnitChange('miles')}
                    data-testid="distance-miles"
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${
                      distanceUnit === 'miles' 
                        ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm' 
                        : 'text-zinc-500 dark:text-zinc-400'
                    }`}
                  >
                    Miles
                  </button>
                </div>
              </div>

              {/* Clock Format */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Clock Format</span>
                </div>
                <div className="flex bg-zinc-100 dark:bg-zinc-700 rounded-lg p-0.5">
                  <button
                    onClick={() => handleClockFormatChange('12')}
                    data-testid="clock-12"
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${
                      clockFormat === '12' 
                        ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm' 
                        : 'text-zinc-500 dark:text-zinc-400'
                    }`}
                  >
                    12h
                  </button>
                  <button
                    onClick={() => handleClockFormatChange('24')}
                    data-testid="clock-24"
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${
                      clockFormat === '24' 
                        ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm' 
                        : 'text-zinc-500 dark:text-zinc-400'
                    }`}
                  >
                    24h
                  </button>
                </div>
              </div>

              {/* Left tile display — mutually-exclusive selector for the
                  Monitor's left flanking tile. Replaces the older
                  standalone "Show check factor" toggle. */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3" data-testid="left-tile-display-row">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                    <div>
                      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Left tile display</span>
                      <p className="text-xs text-zinc-400">Widget in the Monitor&apos;s left slot</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-700 rounded-lg p-1" data-testid="left-tile-display-group">
                  {[
                    { id: 'off', label: 'Off' },
                    { id: 'check_factor', label: 'Check Factor' },
                    { id: 'session_goal', label: 'Session Goal' },
                  ].map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => handleLeftTileChange(o.id)}
                      data-testid={`left-tile-${o.id}`}
                      aria-pressed={leftTileDisplay === o.id}
                      className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                        leftTileDisplay === o.id
                          ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm'
                          : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400 mt-1.5">On Mode 3 (Ergo), Session Goal is always used.</p>
              </div>

              {/* Split smoothing (non-Erg modes) */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3" data-testid="split-smoothing-row">
                <div className="flex items-center gap-2 mb-2">
                  <Gauge className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                  <div>
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Split smoothing</span>
                    <p className="text-xs text-zinc-400">Strokes averaged for the live split · updates at each catch</p>
                  </div>
                </div>
                <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-700 rounded-lg p-1" data-testid="split-smoothing-group">
                  {SPLIT_SMOOTHING_OPTIONS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => handleSplitSmoothingChange(n)}
                      data-testid={`split-smoothing-${n}`}
                      aria-pressed={splitSmoothing === n}
                      className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                        splitSmoothing === n
                          ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm'
                          : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
                      }`}
                    >
                      {n} {n === 1 ? 'stroke' : 'strokes'}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400 mt-1.5">Erg (PM5) splits are not smoothed — the PM5 is the source of truth.</p>
              </div>

              <StrokeSensorLogger />

              <Pm5InfoCard />

              <Pm5GattCard />

              <RawMuxLogger />

              {/* PM5 force curve unit */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="force-unit-row">
                <div className="flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                  <div>
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">PM5 force curve unit</span>
                    <p className="text-xs text-zinc-400">Force axis and peak on the Erg force curve</p>
                  </div>
                </div>
                <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-700 rounded-lg p-1" data-testid="force-unit-group">
                  {FORCE_UNITS.map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => handleForceUnitChange(u)}
                      data-testid={`force-unit-${u}`}
                      aria-pressed={forceUnit === u}
                      className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                        forceUnit === u
                          ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-sm'
                          : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
                      }`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>

              {/* Interval Sounds */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {audio.isMuted ? (
                    <VolumeX className="w-4 h-4 text-zinc-400" />
                  ) : (
                    <Volume2 className="w-4 h-4 text-green-500" />
                  )}
                  <div>
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Interval Sounds</span>
                    <p className="text-xs text-zinc-400">Whistle at start/end of intervals</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => audio.testSound()}
                    className="px-2 py-1 text-xs font-bold text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                    data-testid="test-sound-btn"
                  >
                    Test
                  </button>
                  <button
                    onClick={() => audio.toggleMute()}
                    data-testid="interval-sound-toggle"
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      !audio.isMuted ? 'bg-green-500' : 'bg-zinc-300 dark:bg-zinc-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        !audio.isMuted ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Cox Voice (Race mode voice cues) */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="cox-voice-row">
                <div className="flex items-center gap-2">
                  {coxVoiceEnabled ? (
                    <Mic className="w-4 h-4 text-rose-500" />
                  ) : (
                    <MicOff className="w-4 h-4 text-zinc-400" />
                  )}
                  <div>
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Cox Voice</span>
                    <p className="text-xs text-zinc-400">Spoken encouragement every 500m in Race mode</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleTestCoxVoice}
                    className="px-2 py-1 text-xs font-bold text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 disabled:opacity-40"
                    data-testid="cox-voice-test-btn"
                    disabled={!coxVoiceEnabled}
                  >
                    Test
                  </button>
                  <button
                    onClick={handleToggleCoxVoice}
                    data-testid="cox-voice-toggle"
                    aria-checked={coxVoiceEnabled}
                    role="switch"
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      coxVoiceEnabled ? 'bg-rose-500' : 'bg-zinc-300 dark:bg-zinc-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        coxVoiceEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Cox Voice Profile (2 male, 2 female motivational voices) */}
              <div
                className={`bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3 ${coxVoiceEnabled ? '' : 'opacity-50 pointer-events-none'}`}
                data-testid="cox-voice-profile-row"
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Voice Profile</span>
                    <p className="text-xs text-zinc-400">Tap a profile to hear a sample</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {COX_VOICE_PROFILES.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handlePickVoiceProfile(p.id)}
                      data-testid={`cox-voice-profile-${p.id}`}
                      className={`px-3 py-2 rounded-lg border-2 text-left text-xs transition-colors ${
                        coxVoiceProfile === p.id
                          ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'
                          : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-400 text-zinc-700 dark:text-zinc-200'
                      }`}
                    >
                      <span className="block font-bold uppercase tracking-wider">{p.label}</span>
                      <span className="block text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5 normal-case">
                        Pitch {p.pitch.toFixed(2)} · Rate {p.rate.toFixed(2)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Dark Theme */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {darkMode ? (
                    <Moon className="w-4 h-4 text-indigo-500" />
                  ) : (
                    <Sun className="w-4 h-4 text-amber-500" />
                  )}
                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Dark Theme</span>
                </div>
                <button
                  onClick={toggleDarkMode}
                  data-testid="dark-mode-toggle"
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    darkMode ? 'bg-indigo-600' : 'bg-zinc-300 dark:bg-zinc-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      darkMode ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Connected Apps Section */}
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-3 text-zinc-700 dark:text-zinc-300" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Connected Apps
            </h2>
            <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden">
              {/* Strava - Real Integration */}
              <div className="p-4 border-b border-zinc-100 dark:border-zinc-700" data-testid="strava-connection-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#FC4C02">
                        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.599h4.172L10.463 0l-7 13.828h4.169"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-zinc-800 dark:text-white">Strava</h3>
                      {stravaConnected ? (
                        <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Connected as {stravaAthlete || 'Athlete'}
                        </p>
                      ) : (
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">Upload workouts directly to Strava</p>
                      )}
                    </div>
                  </div>
                  {stravaConnected ? (
                    <button
                      onClick={disconnectStrava}
                      className="px-4 py-2 border-2 border-zinc-200 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 rounded-lg text-sm font-bold hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors flex items-center gap-2"
                      data-testid="strava-disconnect-btn"
                    >
                      <Unlink className="w-4 h-4" />
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={connectStrava}
                      disabled={stravaLoading}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                      data-testid="strava-connect-btn"
                    >
                      {stravaLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Connecting...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4" />
                          Connect
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Intervals.icu - Real Integration */}
              <div className="p-4 border-b border-zinc-100 dark:border-zinc-700" data-testid="intervals-connection-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#6366F1">
                        <rect x="2" y="4" width="4" height="16" rx="1"/>
                        <rect x="8" y="8" width="4" height="12" rx="1"/>
                        <rect x="14" y="2" width="4" height="18" rx="1"/>
                        <rect x="20" y="6" width="2" height="14" rx="1"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-zinc-800 dark:text-white">Intervals.icu</h3>
                      {intervalsConnected ? (
                        <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Connected as {intervalsAthlete || 'Athlete'}
                        </p>
                      ) : (
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">Best rowing analytics - Free</p>
                      )}
                    </div>
                  </div>
                  {intervalsConnected ? (
                    <button
                      onClick={disconnectIntervals}
                      className="px-4 py-2 border-2 border-zinc-200 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 rounded-lg text-sm font-bold hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors flex items-center gap-2"
                      data-testid="intervals-disconnect-btn"
                    >
                      <Unlink className="w-4 h-4" />
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowIntervalsApiKeyInput(!showIntervalsApiKeyInput)}
                      className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-bold hover:bg-indigo-600 transition-colors flex items-center gap-2"
                      data-testid="intervals-connect-btn"
                    >
                      <Link2 className="w-4 h-4" />
                      Connect
                    </button>
                  )}
                </div>
                
                {/* API Key Input Form */}
                {showIntervalsApiKeyInput && !intervalsConnected && (
                  <div className="mt-4 p-4 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                    <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-3">
                      Get your API key from{' '}
                      <a 
                        href="https://intervals.icu/settings" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-indigo-600 dark:text-indigo-400 underline hover:no-underline"
                      >
                        Intervals.icu Settings
                      </a>
                      {' → Developer Settings'}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={intervalsApiKey}
                        onChange={(e) => setIntervalsApiKey(e.target.value)}
                        placeholder="Paste your API key here"
                        className="flex-1 px-3 py-2 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-800 dark:text-white text-sm focus:outline-none focus:border-indigo-500"
                        data-testid="intervals-api-key-input"
                      />
                      <button
                        onClick={connectIntervals}
                        disabled={intervalsLoading || !intervalsApiKey.trim()}
                        className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-bold hover:bg-indigo-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                        data-testid="intervals-save-key-btn"
                      >
                        {intervalsLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Connecting...
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* GPS Info Notice */}
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex gap-3">
              <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-blue-800 dark:text-blue-300 text-sm">GPS Fallback</h3>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  <strong>Auto mode:</strong> Uses the CREWZ case GPS first (u-blox SAM-M10Q, 25 Hz multi-GNSS via BLE). If the case is not connected, automatically falls back to your phone&apos;s built-in GPS.
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  <strong>CREWZ Case:</strong> 25 Hz multi-GNSS receiver inside the electronics bay. <strong>Phone:</strong> Uses your device&apos;s built-in GPS.
                </p>
              </div>
            </div>
          </div>

          {/* Browser Notice */}
          <div className="mt-6 bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-800 dark:text-amber-300 text-sm">Browser Requirements</h3>
                <ul className="text-sm text-amber-700 dark:text-amber-400 mt-2 space-y-1">
                  <li>• <strong>PM5 & HR:</strong> Chrome, Edge, or Opera (HTTPS required)</li>
                  <li>• <strong>GPS/Serial:</strong> Chrome or Edge with experimental features enabled</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      </div>
      <CompanyFooter />
    </div>
  );
}
